'use client';

import React, { useState } from 'react';
import Link from 'next/link';

import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import GameEmbed from './GameEmbed';
import SimilarGames from './SimilarGames';
import ReportModal from './ReportModal';

const GAME = {
  id: 'game-dragon-quest',
  title: 'Dragon Quest AI',
  description: 'Um RPG de fantasia gerado inteiramente por IA onde explores masmorras, lutas contra monstros épicos e descobres tesouros escondidos. O jogo apresenta um sistema de progressão completo com níveis, inventário e missões secundárias. Cada sessão é única graças ao gerador procedural de masmorras.',
  category: 'RPG',
  tags: ['rpg', 'fantasia', 'masmorra', 'aventura', 'pixel-art', 'procedural'],
  plays: 12800,
  likes: 891,
  favorites: 234,
  model: 'Advanced',
  version: 'v2.1',
  createdAt: '02/09/2026',
  updatedAt: '07/09/2026',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_161962b04-1767382628905.png",
  thumbnailAlt: 'Fantasy RPG game with dragon and medieval castle in epic battle scene',
  author: {
    username: 'forge_creator',
    displayName: 'Forge Creator',
    avatar: 'FC',
    bio: 'Criador de jogos RPG e aventura com IA. Apaixonado por pixel art e mecânicas de jogabilidade profundas.',
    gamesCount: 8,
    totalLikes: 2340,
    joinedAt: 'Janeiro 2026'
  }
};

export default function GamePublicScreen() {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(GAME.likes);
  const [favorited, setFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(GAME.favorites);
  const [showReport, setShowReport] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount((prev) => liked ? prev - 1 : prev + 1);
  };

  const handleFavorite = () => {
    setFavorited(!favorited);
    setFavoriteCount((prev) => favorited ? prev - 1 : prev + 1);
  };

  const handleCopyLink = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
        <Link href="/explore" className="hover:text-foreground transition-colors">Explorar</Link>
        <Icon name="ChevronRightIcon" size={12} />
        <span className="text-muted-foreground">{GAME.category}</span>
        <Icon name="ChevronRightIcon" size={12} />
        <span className="text-foreground font-medium">{GAME.title}</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="xl:col-span-2 space-y-6">
          {/* Game title + meta */}
          <div>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{GAME.title}</h1>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge variant="published" />
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/15 text-accent`}>
                    ForgeAI Advanced
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{GAME.version}</span>
                  <span className="text-xs text-muted-foreground">{GAME.category}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 active:scale-95 ${
                  liked ? 'bg-danger/15 text-danger border border-danger/30' : 'bg-card border border-border text-muted-foreground hover:text-danger hover:border-danger/30'}`
                  }>
                  
                  <Icon name="HeartIcon" size={16} variant={liked ? 'solid' : 'outline'} />
                  <span className="tabular-nums">{likeCount.toLocaleString('pt-PT')}</span>
                </button>
                <button
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 active:scale-95 ${
                  favorited ? 'bg-warning/15 text-warning border border-warning/30' : 'bg-card border border-border text-muted-foreground hover:text-warning hover:border-warning/30'}`
                  }>
                  
                  <Icon name="BookmarkIcon" size={16} variant={favorited ? 'solid' : 'outline'} />
                  <span className="tabular-nums">{favoriteCount}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Game embed */}
          <GameEmbed
            thumbnail={GAME.thumbnail}
            thumbnailAlt={GAME.thumbnailAlt}
            title={GAME.title}
            isPlaying={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onStop={() => setIsPlaying(false)} />
          

          {/* Description */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-base font-semibold text-foreground mb-3">Sobre o jogo</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">{GAME.description}</p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-5">
              {GAME.tags.map((tag) =>
              <span key={`tag-${tag}`} className="text-xs px-2.5 py-1 rounded-full bg-muted/50 border border-border text-muted-foreground">
                  #{tag}
                </span>
              )}
            </div>

            {/* Metadata grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border">
              {[
              { label: 'Criado em', value: GAME.createdAt },
              { label: 'Atualizado', value: GAME.updatedAt },
              { label: 'Versão', value: GAME.version },
              { label: 'Categoria', value: GAME.category }].
              map((meta) =>
              <div key={`meta-${meta.label}`}>
                  <p className="text-xs text-muted-foreground mb-0.5">{meta.label}</p>
                  <p className="text-sm font-medium text-foreground">{meta.value}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Stats card */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Estatísticas</h3>
            <div className="space-y-3">
              {[
              { icon: 'PlayIcon', label: 'Jogadas totais', value: GAME.plays.toLocaleString('pt-PT'), color: 'text-primary' },
              { icon: 'HeartIcon', label: 'Likes', value: likeCount.toLocaleString('pt-PT'), color: 'text-danger' },
              { icon: 'BookmarkIcon', label: 'Favoritos', value: favoriteCount.toString(), color: 'text-warning' }].
              map((stat) =>
              <div key={`stat-${stat.label}`} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={15} className={stat.color} />
                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground tabular-nums">{stat.value}</span>
                </div>
              )}
            </div>
          </div>

          {/* Author card */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Criador</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">{GAME.author.avatar}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{GAME.author.displayName}</p>
                <p className="text-xs text-muted-foreground">@{GAME.author.username}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{GAME.author.bio}</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-2.5 rounded-xl bg-muted/30">
                <p className="text-lg font-bold text-foreground tabular-nums">{GAME.author.gamesCount}</p>
                <p className="text-xs text-muted-foreground">Jogos</p>
              </div>
              <div className="text-center p-2.5 rounded-xl bg-muted/30">
                <p className="text-lg font-bold text-foreground tabular-nums">{GAME.author.totalLikes.toLocaleString('pt-PT')}</p>
                <p className="text-xs text-muted-foreground">Likes</p>
              </div>
            </div>
            <Link
              href={`/profile/${GAME.author.username}`}
              className="w-full py-2 rounded-xl text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
            >
              Ver perfil
            </Link>
          </div>

          {/* Actions */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Ações</h3>
            <div className="space-y-2">
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150">
                
                <Icon name={copied ? 'CheckIcon' : 'LinkIcon'} size={16} className={copied ? 'text-success' : ''} />
                {copied ? 'Link copiado!' : 'Copiar link'}
              </button>
              <Link
                href="/create-game"
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150">
                
                <Icon name="DocumentDuplicateIcon" size={16} />
                Criar jogo semelhante
              </Link>
              <button
                onClick={() => setShowReport(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all duration-150">
                
                <Icon name="FlagIcon" size={16} />
                Denunciar jogo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Similar games */}
      <SimilarGames currentGameId={GAME.id} />

      {showReport &&
      <ReportModal onClose={() => setShowReport(false)} gameTitle={GAME.title} />
      }
    </div>);

}